import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Plus, Star, Trash2 } from 'lucide-react';

import GenericTable from 'components/display/Table/Table';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';

import { useCurrentAccess } from 'features/access/hooks/useCurrentAccess';
import {
  PortefeuilleEntryDto,
  getPortefeuille,
  removeFromPortefeuille,
} from '../api/portefeuillePartageApi';
import { AddToPortefeuilleForm } from './AddToPortefeuilleForm';
import { sirenOrSiretLabel } from 'shared/utils/sirenSiret';

export const PortefeuillePartagePage: React.FC = () => {
  const { plateformes, isReady } = useCurrentAccess();
  const { openPanel } = usePanel();

  // 1 PM = 1 OU max comme owner → on prend la première trouvée
  const ownedOu = useMemo(
    () => plateformes.find((o) => o.gouverne) ?? null,
    [plateformes],
  );

  const [entries, setEntries] = useState<PortefeuilleEntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [entryToRemove, setEntryToRemove] = useState<PortefeuilleEntryDto | null>(null);

  const refresh = useCallback(async () => {
    if (!ownedOu) return;
    setLoading(true);
    try {
      const data = await getPortefeuille(ownedOu.plateformeId);
      setEntries(data.entries ?? []);
    } catch {
      toasterService.showToast({
        message: 'Erreur de chargement du portefeuille.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setLoading(false);
    }
  }, [ownedOu]);

  useEffect(() => {
    if (isReady && ownedOu) refresh();
  }, [isReady, ownedOu, refresh]);

  const openAddForm = useCallback(() => {
    if (!ownedOu) return;
    openPanel(
      { title: 'Ajouter au portefeuille partagé', size: Sizes.xl },
      <AddToPortefeuilleForm plateformeId={ownedOu.plateformeId} onSuccess={refresh} />,
    );
  }, [openPanel, ownedOu, refresh]);

  const handleRemove = useCallback(async () => {
    if (!ownedOu || !entryToRemove) return;
    const res = await removeFromPortefeuille(ownedOu.plateformeId, entryToRemove.personneId);
    if (!res.isSuccess) {
      toasterService.showToast({
        message: res.errorMessage ?? 'Échec du retrait',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
      return;
    }
    toasterService.showToast({
      message: `${entryToRemove.designation ?? 'Personne morale'} retirée du portefeuille.`,
      displayType: DisplayTypeEnum.Toaster,
      color: GenericColors.Green,
    });
    setEntryToRemove(null);
    await refresh();
  }, [ownedOu, entryToRemove, refresh]);

  const { createMenuItem } = useActionMenu();

  const createMenuItems = useCallback(
    (entry: PortefeuilleEntryDto) => [
      createMenuItem(
        'remove',
        'Retirer du portefeuille',
        () => setEntryToRemove(entry),
        { icon: 'trash', variant: 'danger' },
      ),
    ],
    [createMenuItem],
  );

  const tableConfig = useMemo(
    () => ({
      title: {
        label: ownedOu
          ? `Portefeuille partagé — ${ownedOu.displayName || 'plateforme'} (${entries.length})`
          : 'Portefeuille partagé',
      },
      data: entries,
      loading,
      columns: [
        {
          key: 'designation' as const,
          label: 'Personne morale',
          sortable: true,
          render: (_: any, row: PortefeuilleEntryDto) => (
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="text-primary" size={18} />
                {row.isPartnerPlateforme && (
                  <Star
                    size={10}
                    className="absolute -top-1 -left-1 fill-amber-400 text-amber-400 drop-shadow-sm"
                  />
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="font-medium text-gray-900">{row.designation || '—'}</span>
                <span className="text-xs text-gray-500">{sirenOrSiretLabel(row.numeroSiret)}: {row.numeroSiret || '—'}</span>
                {row.isPartnerPlateforme && (
                  <span className="text-[10px] text-amber-700 uppercase tracking-wide font-semibold">
                    ★ Partenaire
                  </span>
                )}
              </div>
            </div>
          ),
        },
        {
          key: 'addedAt' as const,
          label: 'Ajoutée le',
          render: (_: any, row: PortefeuilleEntryDto) =>
            new Date(row.addedAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }),
        },
      ],
      actions: {
        create: { label: 'Ajouter au portefeuille', onClick: openAddForm },
        row: [
          {
            label: 'moreMenu',
            render: (row: PortefeuilleEntryDto, index: number) => (
              <DropdownButton
                key={index}
                width='w-64'
                anchor='bottom'
                items={createMenuItems(row)}
              />
            ),
          },
        ],
      },
    }),
    [ownedOu, entries, loading, openAddForm, createMenuItems],
  );

  if (!isReady) {
    return <SimpleDashboardContent>Chargement…</SimpleDashboardContent>;
  }

  if (!ownedOu) {
    return (
      <SimpleDashboardContent>
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <Star className="mx-auto text-gray-300 mb-3" size={32} />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Pas d&apos;plateforme à gérer
          </h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Votre personne morale n&apos;est pas désignée comme principale d&apos;une plateforme.
            Cette page permet aux PM principales de gérer le portefeuille partagé de leur plateforme.
          </p>
        </div>
      </SimpleDashboardContent>
    );
  }

  return (
    <SimpleDashboardContent>
      <GenericTable config={tableConfig} />

      <ConfirmationOverlay
        isOpen={!!entryToRemove}
        onClose={() => setEntryToRemove(null)}
        onConfirm={handleRemove}
        title="Retirer du portefeuille"
        description={
          entryToRemove
            ? `Confirmer le retrait de ${entryToRemove.designation ?? 'cette personne morale'} ? Elle ne sera plus visible par les autres membres de la plateforme. Les ressources déjà créées restent intactes.`
            : ''
        }
        confirmLabel="Retirer"
        cancelLabel="Annuler"
        variant="danger"
      />
    </SimpleDashboardContent>
  );
};
